import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useNotificationContext } from "../context/NotificationContext";
import {
  LayoutDashboard,
  LogOut,
  CreditCard,
  Building2,
  Stethoscope,
  ClipboardList,
  FileText,
  ShieldCheck,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  User,
  Bell,
} from "lucide-react";
import clsx from "clsx";

const getPageTitle = (pathname: string): string => {
  if (pathname.startsWith("/profile")) return "Profile";
  if (pathname.startsWith("/hospitals")) {
    if (pathname.includes("/new")) return "Add Hospital";
    if (pathname.includes("/edit")) return "Edit Hospital";
    if (/\/hospitals\/[^/]+$/.test(pathname)) return "Hospital Details";
    return "Hospitals";
  }
  if (pathname.startsWith("/doctors")) {
    if (pathname.includes("/new")) return "Add Doctor";
    if (pathname.includes("/edit")) return "Edit Doctor";
    return "Doctors";
  }
  if (pathname.startsWith("/mdt-templates")) return "MDT Templates";
  if (pathname.startsWith("/audit-logs")) return "Audit Logs";
  if (pathname.startsWith("/legal-content/edit")) return "Edit Legal Content";
  if (pathname.startsWith("/legal-content")) return "Legal Content";
  if (pathname.startsWith("/super-admin/subscriptions/dashboard") || pathname.startsWith("/super-admin/subscription-dashboard")) return "Subscription Dashboard";
  if (pathname.startsWith("/super-admin/subscriptions") || pathname.startsWith("/super-admin/subscription-listing")) return "Subscription Listing";
  if (pathname.startsWith("/super-admin/subscription")) {
    if (pathname.includes("/new")) return "Add Subscription Plan";
    if (pathname.includes("/edit")) return "Edit Subscription Plan";
    return "Plans & Pricing";
  }
  return "Dashboard";
};

const SuperAdminLayout = () => {
  const { unreadCount } = useNotificationContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Mobile drawer open state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Desktop collapsible sidebar state (persisted in localStorage)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem("superadmin_sidebar_collapsed") === "true";
  });

  const toggleCollapsed = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("superadmin_sidebar_collapsed", String(next));
      return next;
    });
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  const isSubscriptionsActive =
    location.pathname.startsWith("/super-admin/subscription") ||
    location.pathname.startsWith("/super-admin/subscriptions") ||
    location.pathname.startsWith("/super-admin/subscription-listing") ||
    location.pathname.startsWith("/super-admin/subscription-dashboard");

  const isSubDashboardActive =
    location.pathname.startsWith("/super-admin/subscriptions/dashboard") ||
    location.pathname.startsWith("/super-admin/subscription-dashboard");

  const isListingActive =
    !isSubDashboardActive &&
    (location.pathname.startsWith("/super-admin/subscriptions") ||
      location.pathname.startsWith("/super-admin/subscription-listing"));

  const isPlansActive = isSubscriptionsActive && !isListingActive && !isSubDashboardActive;

  const isDashboardActive =
    (location.pathname === "/dashboard" ||
      location.pathname === "/super-admin" ||
      location.pathname.startsWith("/dashboard/")) &&
    !isSubscriptionsActive;

  const [subscriptionsOpen, setSubscriptionsOpen] = useState(() => isSubscriptionsActive);

  useEffect(() => {
    if (isSubscriptionsActive) {
      setSubscriptionsOpen(true);
    }
  }, [isSubscriptionsActive]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <div className="h-screen flex overflow-hidden bg-[#F7F9FB] font-sans">
      {/* Overlay - mobile only */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          "fixed inset-y-0 left-0 z-40 bg-white text-neutral-700 flex flex-col shadow-xl transition-all duration-300 ease-in-out shrink-0 border-r border-[#E4EAF0]",
          "md:relative md:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          isCollapsed ? "w-72 md:w-20" : "w-72"
        )}
      >
        {/* Branding & Collapse Toggle */}
        <div
          className={clsx(
            "border-b border-[#E4EAF0] transition-all duration-300 p-4 md:p-5 flex items-center justify-between",
            isCollapsed && "md:flex-col md:items-center md:gap-2.5"
          )}
        >
          <div
            className={clsx(
              "flex items-center gap-3 overflow-hidden",
              isCollapsed && "md:justify-center"
            )}
          >
            <div className="h-9 w-9 rounded-lg bg-primary-500 flex items-center justify-center shrink-0 shadow-md shadow-primary-500/20">
              <span className="font-bold text-white text-lg">D</span>
            </div>
            <div className={clsx("truncate", isCollapsed && "md:hidden")}>
              <h1 className="text-lg font-bold tracking-tight text-primary-500 leading-tight">
                DiagnomIQ
              </h1>
              <p className="text-[10px] text-[#63788E] uppercase tracking-wider font-semibold">
                Super Admin
              </p>
            </div>
          </div>

          {/* Desktop Toggle Button */}
          <button
            onClick={toggleCollapsed}
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            className="hidden md:flex p-1.5 rounded-lg text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>

          {/* Mobile Close Button (Soft Red & Circular Ring at Top Right) */}
          <button
            onClick={() => setSidebarOpen(false)}
            title="Close Menu"
            className="md:hidden flex items-center justify-center h-8 w-8 rounded-full bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600 border border-red-200 shadow-xs transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 p-3 space-y-1.5 overflow-y-auto overflow-x-hidden">
          <SidebarItem
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            active={isDashboardActive}
            collapsed={isCollapsed}
            onClick={() => navigate("/dashboard")}
          />
          <SidebarItem
            icon={<Building2 size={20} />}
            label="Hospitals"
            active={isActive("/hospitals")}
            collapsed={isCollapsed}
            onClick={() => navigate("/hospitals")}
          />
          <SidebarItem
            icon={<Stethoscope size={20} />}
            label="Doctors"
            active={isActive("/doctors")}
            collapsed={isCollapsed}
            onClick={() => navigate("/doctors")}
          />
          <SidebarItem
            icon={<FileText size={20} />}
            label="MDT Templates"
            active={isActive("/mdt-templates")}
            collapsed={isCollapsed}
            onClick={() => navigate("/mdt-templates")}
          />
          <SidebarItem
            icon={<ShieldCheck size={20} />}
            label="Legal Content"
            active={isActive("/legal-content")}
            collapsed={isCollapsed}
            onClick={() => navigate("/legal-content")}
          />
          <SidebarItem
            icon={<ClipboardList size={20} />}
            label="Audit Logs"
            active={isActive("/audit-logs")}
            collapsed={isCollapsed}
            onClick={() => navigate("/audit-logs")}
          />
          <SidebarItem
            icon={
              <div className="relative">
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#D96F91] border border-white" />
                )}
              </div>
            }
            label="Notifications"
            active={isActive("/notifications")}
            collapsed={isCollapsed}
            onClick={() => navigate("/notifications")}
          />
          {/* Subscriptions Menu with Nested Plans & Pricing and Subscription Listing */}
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => {
                if (isCollapsed) {
                  setIsCollapsed(false);
                  localStorage.setItem("superadmin_sidebar_collapsed", "false");
                  setSubscriptionsOpen(true);
                } else {
                  setSubscriptionsOpen((prev) => !prev);
                }
              }}
              title="Subscriptions"
              className={clsx(
                "w-full flex items-center justify-between rounded-lg text-sm font-medium transition-all duration-200 group relative gap-3.5 px-4 py-3 cursor-pointer",
                isCollapsed && "md:justify-center md:px-3 md:py-3",
                isSubscriptionsActive
                  ? "bg-[#E9F7F5] text-[#15BBAE] font-semibold"
                  : "text-[#63788E] hover:bg-[#F0FAF9]"
              )}
            >
              {isSubscriptionsActive && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r bg-[#15BBAE]" aria-hidden="true" />
              )}
              <div className="flex items-center gap-3.5 min-w-0">
                <span
                  className={clsx(
                    "transition-colors shrink-0",
                    isSubscriptionsActive ? "text-[#15BBAE]" : "text-[#63788E] group-hover:text-[#15BBAE]"
                  )}
                >
                  <CreditCard size={20} />
                </span>
                <span className={clsx("truncate", isCollapsed && "md:hidden")}>Subscriptions</span>
              </div>
              {!isCollapsed && (
                <ChevronDown
                  size={16}
                  className={clsx(
                    "transition-transform duration-200 text-[#63788E] shrink-0",
                    subscriptionsOpen ? "rotate-180 text-[#15BBAE]" : ""
                  )}
                />
              )}
            </button>

            {/* Submenu items */}
            {subscriptionsOpen && !isCollapsed && (
              <div className="space-y-1 pl-11 pr-2 pt-0.5 pb-1">
                <button
                  type="button"
                  onClick={() => navigate("/super-admin/subscriptions/dashboard")}
                  className={clsx(
                    "w-full flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer text-left",
                    isSubDashboardActive
                      ? "bg-[#E9F7F5] text-[#15BBAE] font-semibold"
                      : "text-[#63788E] hover:bg-[#F0FAF9] hover:text-[#15BBAE]"
                  )}
                >
                  Dashboard
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/super-admin/subscription")}
                  className={clsx(
                    "w-full flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer text-left",
                    isPlansActive
                      ? "bg-[#E9F7F5] text-[#15BBAE] font-semibold"
                      : "text-[#63788E] hover:bg-[#F0FAF9] hover:text-[#15BBAE]"
                  )}
                >
                  Plans & Pricing
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/super-admin/subscriptions")}
                  className={clsx(
                    "w-full flex items-center px-3 py-2 text-xs font-medium rounded-lg transition-colors cursor-pointer text-left",
                    isListingActive
                      ? "bg-[#E9F7F5] text-[#15BBAE] font-semibold"
                      : "text-[#63788E] hover:bg-[#F0FAF9] hover:text-[#15BBAE]"
                  )}
                >
                  Subscription Listing
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* Footer Actions / Profile & Sign Out */}
        <div className="p-3 border-t border-[#E4EAF0] space-y-2">
          <button
            type="button"
            onClick={() => {
              navigate("/profile");
              setSidebarOpen(false);
            }}
            title={isCollapsed ? "View Profile" : undefined}
            className={clsx(
              "w-full text-left transition-colors duration-200 cursor-pointer rounded-lg p-2 flex items-center gap-3",
              isCollapsed ? "md:justify-center md:px-0" : "",
              location.pathname === "/profile"
                ? "bg-primary-50 border border-primary-100"
                : "hover:bg-neutral-100"
            )}
          >
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 text-primary-600">
              <User size={16} />
            </div>
            {!isCollapsed && (
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate text-neutral-800 hover:text-primary-600 transition-colors">
                  {user?.name ?? "Super Admin"}
                </div>
                <div className="text-neutral-500 capitalize text-xs truncate">
                  {user?.roleName || (user?.role ?? "Super Admin").replace(/_/g, " ")}
                </div>
              </div>
            )}
          </button>

          <button
            onClick={logout}
            title={isCollapsed ? "Sign Out" : undefined}
            className={clsx(
              "flex items-center w-full text-sm font-medium text-[#D96F91] rounded-lg bg-[#FFF8F9] border border-[#F6DCE2] hover:bg-[#FFF0F3] hover:text-[#D96F91] transition-all duration-200 group gap-3 px-4 py-3",
              isCollapsed && "md:justify-center md:px-3 md:py-3"
            )}
          >
            <LogOut size={20} className="shrink-0 text-[#D96F91] transition-colors" />
            <span
              className={clsx(
                "text-[#D96F91] transition-colors truncate",
                isCollapsed && "md:hidden"
              )}
            >
              Sign Out
            </span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        {/* Desktop Header Top Bar */}
        <header className="hidden md:flex items-center justify-between px-6 py-3.5 bg-white border-b border-[#E4EAF0] sticky top-0 z-20 shadow-xs">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-[#10253A]">
              {getPageTitle(location.pathname)}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
            type="button"
            onClick={() => navigate("/profile")}
            className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-neutral-50 border border-neutral-200 transition-colors cursor-pointer"
          >
            <div className="w-7 h-7 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
              <User size={14} />
            </div>
            <span className="text-xs font-semibold text-neutral-700">
              {user?.name || "Super Admin"}
            </span>
          </button>
          </div>
        </header>

        {/* Mobile Header */}
        <div className="sticky top-0 z-20 bg-white border-b border-[#E4EAF0] md:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 transition-colors"
              >
                <Menu size={20} />
              </button>
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-primary-500 flex items-center justify-center shrink-0">
                  <span className="font-bold text-white text-sm">D</span>
                </div>
                <span className="text-sm font-bold text-primary-500">DiagnomIQ</span>
                <span className="text-[11px] font-medium text-gray-400 ml-1">/ {getPageTitle(location.pathname)}</span>
              </div>
            </div>
            
          </div>
        </div>

        {/* Main Body View */}
        <div className="w-full p-4 sm:p-5 md:p-6 flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

/* --- Sidebar Item Component --- */
const SidebarItem = ({
  icon,
  label,
  badge,
  active,
  collapsed,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  badge?: React.ReactNode;
  active?: boolean;
  collapsed?: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    title={label}
    className={clsx(
      "w-full flex items-center rounded-lg text-sm font-medium transition-all duration-200 group relative gap-3.5 px-4 py-3",
      collapsed && "md:justify-center md:px-3 md:py-3",
      active
        ? "bg-[#E9F7F5] text-[#15BBAE] font-semibold"
        : "text-[#63788E] hover:bg-[#F0FAF9]"
    )}
  >
    {active && (
      <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r bg-[#15BBAE]" aria-hidden="true" />
    )}
    <span
      className={clsx(
        "transition-colors shrink-0",
        active ? "text-[#15BBAE]" : "text-[#63788E] group-hover:text-[#15BBAE]"
      )}
    >
      {icon}
    </span>
    <span className={clsx("truncate flex-1 text-left", collapsed && "md:hidden")}>{label}</span>
    {!collapsed && badge && (
      <span className="shrink-0">{badge}</span>
    )}
  </button>
);

export default SuperAdminLayout;
